/**
 * @typedef {import('@converse/headless/types/shared/message').default} BaseMessage
 * @typedef {import('@converse/headless/types/shared/types').ChatBoxOrMUC} ChatBoxOrMUC
 */
import { _converse, api, converse, log, u } from '@converse/headless';
import { __ } from 'i18n';
import { buildReactionFallbackBody } from './fallback.js';
const { Strophe, sizzle, stx } = converse.env;

/**
 * Delegate to {@link PopularEmojis#getPopularEmojis} to avoid duplicating the
 * sorted-emojis + defaults-fallback logic.
 * @param {string[]} allowed_emojis
 * @returns {Promise<string[]>}
 */
export async function getPopularReactions(allowed_emojis) {
    const popular_emojis = _converse.state.popular_emojis;
    if (!popular_emojis) return [];

    const data = await popular_emojis.getPopularEmojis();
    const emojis = Object.keys(data);
    return allowed_emojis ? emojis.filter((e) => allowed_emojis.includes(e)) : emojis;
}

/**
 * Helper function to update a message with reactions (JID-keyed format).
 * Used for optimistic updates when sending reactions.
 *
 * Reactions are stored as `{ jid: [emoji1, emoji2, ...] }`.
 * For MUC the key is the full JID (room@domain/nick), matching what the
 * server will echo back. For 1:1 chats the key is the bare JID.
 *
 * @param {BaseMessage} message - The message model to update
 * @param {string[]} emojis - The list of emojis (can be empty for removal)
 */
export function updateMessageReactions(message, emojis) {
    /** @type {ChatBoxOrMUC} */
    const chatbox = message.collection.chatbox;
    const my_jid = u.reactions.getOwnReactionJID(chatbox);

    const current_reactions = message.get('reactions') || {};
    const reactions = { ...current_reactions };

    if (emojis.length === 0) {
        delete reactions[my_jid];
    } else {
        reactions[my_jid] = emojis;
    }

    message.save({ reactions });
}

/**
 * Send a XEP-0444 reaction stanza and optimistically update the message.
 *
 * The message's `reactions` are updated optimistically (so the UI is
 * responsive); for the OMEMO-encrypted path, if the send fails the optimistic
 * update is rolled back (`api.omemo.send` has already surfaced the error to the
 * user). The cleartext path stays fire-and-forget — a bounced reaction stanza is
 * rolled back later via the `getErrorAttributesForMessage` hook.
 *
 * @param {BaseMessage} message - The message model to update
 * @param {string} emoji - The selected emoji or shortname
 */
export async function sendReaction(message, emoji) {
    if (!emoji) return;

    /** @type {ChatBoxOrMUC} */
    const chatbox = message.collection.chatbox;
    const to_jid = chatbox.get('jid');
    const type = chatbox.get('type') === 'chatroom' ? 'groupchat' : 'chat';

    // For MUC messages, use the MUC-assigned stanza_id as the reaction target id.
    // Fall back to msgid if the MUC stanza_id is not present.
    const muc_stanza_id = type === 'groupchat' ? message.get(`stanza_id ${to_jid}`) : null;
    const msg_id = muc_stanza_id || message.get('msgid');

    let emoji_unicode = emoji;
    if (emoji.startsWith(':') && emoji.endsWith(':')) {
        const emoji_array = u.shortnamesToEmojis(emoji, { unicode_only: true });
        emoji_unicode = Array.isArray(emoji_array) ? emoji_array.join('') : emoji_array;
    }

    if (emoji_unicode.startsWith(':') && emoji_unicode.endsWith(':')) {
        log.error(`sendReaction: could not convert shortname to emoji: ${emoji}`);
        return;
    }

    const my_jid = u.reactions.getOwnReactionJID(chatbox);
    const current_reactions = message.get('reactions') || {};
    // Snapshot our current reaction set so we can roll back if the send fails.
    const previous_emojis = [...(current_reactions[my_jid] || [])];
    const my_reactions = new Set(previous_emojis);

    if (my_reactions.has(emoji_unicode)) {
        my_reactions.delete(emoji_unicode);
    } else {
        my_reactions.add(emoji_unicode);
    }

    const new_emojis = Array.from(my_reactions);

    // Optimistic update — keeps the UI responsive while the send is in flight.
    updateMessageReactions(message, new_emojis);

    // When adding a reaction (not removing), bump it to the front of the popular
    // emojis list and schedule a debounced publish to the user's PEP node.
    if (my_reactions.has(emoji_unicode)) {
        _converse.state.popular_emojis?.recordUsage([emoji_unicode]);
    }

    // Reactions are body-coupled metadata: when the chat is OMEMO-encrypted we
    // must not leak them in cleartext, so route them through OMEMO. Otherwise
    // send the plain XEP-0444 stanza.
    if (chatbox.get('omemo_active')) {
        try {
            await sendEncryptedReaction(chatbox, message, msg_id, new_emojis);
        } catch (e) {
            // api.omemo.send already alerted the user; undo the optimistic update.
            log.error(e);
            updateMessageReactions(message, previous_emojis);
        }
    } else {
        api.send(stx`
            <message to="${to_jid}" type="${type}" id="${u.getUniqueId('reaction')}" xmlns="jabber:client">
                <reactions xmlns="${Strophe.NS.REACTIONS}" id="${msg_id}">
                    ${new_emojis.map((reaction) => stx`<reaction>${reaction}</reaction>`)}
                </reactions>
                <store xmlns="${Strophe.NS.HINTS}"/>
            </message>
        `);
    }
}

export { buildReactionFallbackBody };

/**
 * Send a XEP-0444 reaction encrypted via OMEMO.
 *
 * The structured `<reactions>` element travels encrypted inside the omemo:2 SCE
 * `<content>`; the body — a quote of the reacted-to message plus the emoji(s)
 * (see {@link buildReactionFallbackBody}) — doubles as the fallback for
 * legacy-OMEMO recipients, whose payload can't carry structured metadata. A
 * XEP-0428 `<fallback for="urn:xmpp:reactions:0">` marks the whole body so
 * XEP-0444-aware clients strip it and render the reaction natively instead.
 *
 * @param {ChatBoxOrMUC} chatbox
 * @param {BaseMessage} message - the message being reacted to (for its text)
 * @param {string} msg_id - the id of the message being reacted to
 * @param {string[]} emojis - the user's current reaction set (empty on retraction)
 * @returns {Promise<void>} rejects if the encrypted send fails
 */
function sendEncryptedReaction(chatbox, message, msg_id, emojis) {
    const extensions = [
        stx`<reactions xmlns="${Strophe.NS.REACTIONS}" id="${msg_id}">
            ${emojis.map((reaction) => stx`<reaction>${reaction}</reaction>`)}
        </reactions>`,
    ];
    const body = buildReactionFallbackBody(message.getMessageText(), emojis);
    // A retraction (empty set) has no body, so there's nothing to mark.
    if (body) {
        extensions.push(stx`<fallback xmlns="${Strophe.NS.FALLBACK}" for="${Strophe.NS.REACTIONS}"><body/></fallback>`);
    }
    return api.omemo.send(chatbox, body, extensions);
}

/**
 * Convert JID-keyed reactions to emoji-keyed format for display.
 *
 * Input:  `{ jid1: ['👍', '❤️'], jid2: ['👍'] }`
 * Output: `{ '👍': ['jid1', 'jid2'], '❤️': ['jid1'] }`
 *
 * @param {Record<string, string[]>} reactions - JID-keyed reactions map
 * @returns {Record<string, string[]>} - Emoji-keyed map for display
 */
export function getEmojiKeyedReactions(reactions) {
    /** @type {Record<string, string[]>} */
    const emoji_map = {};
    for (const jid in reactions) {
        for (const emoji of reactions[jid]) {
            if (!emoji_map[emoji]) {
                emoji_map[emoji] = [];
            }
            emoji_map[emoji].push(jid);
        }
    }
    return emoji_map;
}

/**
 * Resolves a list of reactor JIDs to human-readable display names.
 *
 * For MUC, the key is a full JID (room@domain/nick) — the nick is extracted
 * directly from the resource part. For 1:1 chats the key is a bare JID and
 * we look up the roster contact for the best available name.
 *
 * Returns a formatted string such as:
 *   "Alice"
 *   "Alice and Bob"
 *   "Alice, Bob and 1 other"
 *   "Alice, Bob and 3 others"
 *
 * @param {string[]} jids - Reactor JIDs (MUC full JIDs or 1:1 bare JIDs)
 * @param {ChatBoxOrMUC} chatbox - The chatbox model
 * @returns {Promise<string>}
 */
export async function getReactorNames(jids, chatbox) {
    const is_muc = chatbox.get('type') === 'chatroom';
    const own_bare_jid = _converse.session.get('bare_jid');

    /**
     * @param {string} key - occupant_id, bare JID, or full JID
     * @returns {Promise<string>}
     */
    const resolve = async (key) => {
        if (is_muc) {
            // Try to look up the occupant by occupant_id first, then by bare JID,
            // then fall back to extracting the nick from the resource part of a full JID.
            const occupants = chatbox.occupants;
            const by_occupant_id = occupants?.findWhere({ 'occupant_id': key });
            if (by_occupant_id) return by_occupant_id.get('nick') || key;
            const by_jid = occupants?.findWhere({ 'jid': key });
            if (by_jid) return by_jid.get('nick') || key;
            return Strophe.getResourceFromJid(key) || key;
        }
        if (Strophe.getBareJidFromJid(key) === own_bare_jid) {
            // The reactor is the logged-in user — look up the profile directly,
            // since our own JID is not present in the roster contacts list.
            return _converse.state.profile?.getDisplayName() ?? key;
        }
        const contact = await api.contacts.get(key);
        return contact?.getDisplayName() ?? key;
    };

    const max_named = 2;
    const named = await Promise.all(jids.slice(0, max_named).map(resolve));
    const remainder = jids.length - named.length;

    if (remainder === 0) {
        // "Alice" or "Alice and Bob"
        return named.length === 1 ? named[0] : __('%1$s and %2$s', named[0], named[1]);
    }
    // "Alice, Bob and 1 other" / "Alice, Bob and 3 others"
    const others_str =
        remainder === 1
            ? __('%1$s and 1 other', named.join(', '))
            : __('%1$s and %2$d others', named.join(', '), remainder);
    return others_str;
}

/** @param {Element} stanza */
async function handleRestrictedReactions(stanza) {
    const query = sizzle(`query[xmlns="${Strophe.NS.DISCO_INFO}"]`, stanza).pop();
    if (!query) {
        return;
    }

    // Per XEP-0444 §2.2, restricted reactions are advertised via a XEP-0128
    // Service Discovery Extensions data form with FORM_TYPE "urn:xmpp:reactions:0:restrictions"
    // and an "allowlist" field whose <value> children list the permitted emojis.
    const form = sizzle(`x[xmlns="jabber:x:data"]`, query).pop();
    if (!form) {
        return;
    }
    const form_type = sizzle(`field[var="FORM_TYPE"] value`, form).pop();
    if (form_type?.textContent !== 'urn:xmpp:reactions:0:restrictions') {
        return;
    }
    const allowlist_field = sizzle(`field[var="allowlist"]`, form).pop();
    if (!allowlist_field) {
        return;
    }

    const from_jid = stanza.getAttribute('from');
    if (!from_jid) {
        return;
    }

    const bare_jid = Strophe.getBareJidFromJid(from_jid);
    const allowed = Array.from(allowlist_field.querySelectorAll('value'))
        .map((el) => el.textContent)
        .filter(Boolean);

    /** @type {ChatBoxOrMUC|undefined} */
    const chatbox = await api.chatboxes.get(bare_jid);
    chatbox?.save('allowed_reactions', allowed);
}

/**
 * Registers a handler for disco#info result stanzas to check for restricted reactions support.
 */
export function registerRestrictedReactionsHandler() {
    api.connection.get()?.addHandler(
        /** @param {Element} stanza */ (stanza) => {
            handleRestrictedReactions(stanza);
            return true;
        },
        Strophe.NS.DISCO_INFO,
        'iq',
        'result',
    );
}
